import { getJobLocationGroup } from './locationGroups.js';
import { getEffectiveSalary } from './jobSalary.js';

const asTimestamp = value => {
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const asPriority = value => {
  const priority = Number(value);
  return Number.isFinite(priority) ? Math.max(0, priority) : 0;
};

const promotionExpiryTimestamp = value => {
  const raw = String(value || '').trim();
  const timestamp = asTimestamp(raw);
  if (!timestamp) return 0;

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
  }
  return timestamp;
};

export const isPromotionActive = (job, now = Date.now()) => {
  if (job?.is_promoted !== true) return false;
  if (!job.promotion_expires) return true;
  const expiry = promotionExpiryTimestamp(job.promotion_expires);
  return expiry > 0 && expiry >= now;
};

export const FEATURE_OVERRIDE_VALUES = ['automatic', 'featured', 'excluded'];

export const normalizeFeaturedOverride = (value, job = null) => {
  if (FEATURE_OVERRIDE_VALUES.includes(value)) return value;
  return job?.admin_featured === true ? 'featured' : 'automatic';
};

export const getFeaturedOverride = job => normalizeFeaturedOverride(job?.featured_override, job);

// A public Featured badge is an editorial decision. Importer-created is_featured
// flags are retained as automatic homepage candidates, not public endorsements.
export const isFeaturedJob = job => getFeaturedOverride(job) === 'featured';
export const isFeatureExcluded = job => getFeaturedOverride(job) === 'excluded';
export const isAutomaticFeaturedCandidate = job => getFeaturedOverride(job) === 'automatic' && job?.is_featured === true;

export const PUBLIC_JOB_ORDER_VALUES = ['balanced', 'manual', 'newest', 'luxury-score', 'alphabetical'];

export const normalizePublicJobOrder = value => PUBLIC_JOB_ORDER_VALUES.includes(value) ? value : 'balanced';

const presentationTier = (job, now) => {
  if (isPromotionActive(job, now)) return 3;
  if (isFeaturedJob(job)) return 2;
  if (job?.luxury_brand && job.luxury_brand !== 'Unknown') return 1;
  return 0;
};

const smartScore = job => {
  const luxuryScore = Number(job?.luxury_score || 0);
  const recruiterBoost = job?.recruiter ? 2 : 0;
  return (Number.isFinite(luxuryScore) ? luxuryScore : 0) + recruiterBoost;
};

const locationRank = job => {
  switch (getJobLocationGroup(job)) {
    case 'central_london': return 3;
    case 'greater_london_m25': return 2;
    default: return 1;
  }
};

const isLondonJob = job => locationRank(job) >= 2;

const homepageScore = job => {
  const score = Math.min(100, Math.max(0, Number(job?.luxury_score || 0)));
  const priority = Math.min(1000, asPriority(job?.listing_priority));
  const recognisedBrand = job?.luxury_brand && job.luxury_brand !== 'Unknown' ? 300 : 0;
  const locationBoost = locationRank(job) === 3 ? 350 : (locationRank(job) === 2 ? 250 : 0);
  const completeness = [job?.description, getEffectiveSalary(job), job?.luxury_brand, job?.department]
    .filter(value => String(value || '').trim()).length;
  return locationBoost + recognisedBrand + (priority * 10) + score + (completeness * 5);
};

const compareHomepageJobs = (a, b) => {
  const scoreDifference = homepageScore(b) - homepageScore(a);
  if (scoreDifference) return scoreDifference;
  const dateDifference = asTimestamp(b?.date_added || b?.created) - asTimestamp(a?.date_added || a?.created);
  if (dateDifference) return dateDifference;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
};

const homepageDedupeKey = job => [job?.title, job?.store, job?.location]
  .map(value => String(value || '').trim().toLowerCase())
  .join('|');

const homepageFamilyKey = job => {
  const text = `${job?.title || ''} ${job?.department || ''}`.toLowerCase();
  if (/chef|waiter|hospitality|restaurant|food|beverage/.test(text)) return 'hospitality';
  if (/visual merch|marketing|e-?commerce|creative|designer|design |content|social media|communications|brand manager/.test(text)) return 'creative-marketing';
  if (/buyer|buying|merchandis|allocation|planning/.test(text)) return 'buying-merchandising';
  if (/finance|accountant|analyst|operations|supply chain|packaging|technology|\bit\b|project|human resources|\bhr\b|people/.test(text)) return 'corporate-operations';
  if (/sales|client|customer|beauty|retail|store|boutique|supervisor|team manager|host|ambassador|artist/.test(text)) return 'retail-customer';
  return 'other';
};

const appendHomepageJob = (selected, candidate, seen, employerCounts, familyCounts, limit, options = {}) => {
  if (!candidate || selected.length >= limit || isFeatureExcluded(candidate)) return false;
  const key = homepageDedupeKey(candidate);
  if (seen.has(key)) return false;
  const employer = employerKey(candidate);
  const employerLimit = options.relaxEmployerLimit ? Number.POSITIVE_INFINITY : 2;
  if ((employerCounts.get(employer) || 0) >= employerLimit) return false;
  const family = homepageFamilyKey(candidate);
  const familyLimit = options.automatic && !options.relaxFamilyLimit ? 2 : Number.POSITIVE_INFINITY;
  if ((familyCounts.get(family) || 0) >= familyLimit) return false;
  if (options.londonOnly && !isLondonJob(candidate)) return false;
  if (options.automatic && !isLondonJob(candidate) && options.outsideCount >= 1) return false;
  selected.push(candidate);
  seen.add(key);
  employerCounts.set(employer, (employerCounts.get(employer) || 0) + 1);
  familyCounts.set(family, (familyCounts.get(family) || 0) + 1);
  return true;
};

/**
 * Selects the homepage showcase independently from the public Jobs-page order.
 * Promotions and explicit editorial choices lead. Automatic slots are London-
 * first, allow at most one regional role, and avoid employer repetition.
 */
export const selectHomepageJobs = (jobs, limit = 6, now = Date.now()) => {
  const usable = jobs.filter(job => job?.visible !== false && String(job?.status || 'active').toLowerCase() !== 'expired' && !isFeatureExcluded(job));
  const promoted = usable.filter(job => isPromotionActive(job, now)).sort((a, b) => compareManualJobs(a, b, now));
  const editorial = usable.filter(job => !isPromotionActive(job, now) && isFeaturedJob(job)).sort(compareHomepageJobs);
  const automatic = usable.filter(job => !isPromotionActive(job, now) && !isFeaturedJob(job) && isAutomaticFeaturedCandidate(job)).sort(compareHomepageJobs);
  const fallback = usable.filter(job => !isPromotionActive(job, now) && !isFeaturedJob(job) && !isAutomaticFeaturedCandidate(job)).sort(compareHomepageJobs);
  const selected = [];
  const seen = new Set();
  const employerCounts = new Map();
  const familyCounts = new Map();

  for (const job of [...promoted, ...editorial]) appendHomepageJob(selected, job, seen, employerCounts, familyCounts, limit);

  // Keep the initial three cards London-based whenever automatic London roles exist.
  for (const job of automatic) {
    if (selected.length >= Math.min(3, limit)) break;
    appendHomepageJob(selected, job, seen, employerCounts, familyCounts, limit, { londonOnly: true, automatic: true });
  }

  let outsideCount = selected.filter(job => !isLondonJob(job) && !isPromotionActive(job, now) && !isFeaturedJob(job)).length;
  for (const pool of [automatic, fallback]) {
    for (const job of pool) {
      const appended = appendHomepageJob(selected, job, seen, employerCounts, familyCounts, limit, { automatic: true, outsideCount });
      if (appended && !isLondonJob(job)) outsideCount += 1;
      if (selected.length >= limit) return selected;
    }
  }

  // Only relax employer diversity when the catalogue cannot otherwise fill the showcase.
  for (const pool of [automatic, fallback]) {
    for (const job of pool) {
      const appended = appendHomepageJob(selected, job, seen, employerCounts, familyCounts, limit, { automatic: true, outsideCount, relaxEmployerLimit: true, relaxFamilyLimit: true });
      if (appended && !isLondonJob(job)) outsideCount += 1;
      if (selected.length >= limit) return selected;
    }
  }

  return selected;
};

export const getHomepageSelectionLabel = (job, now = Date.now()) => {
  if (isPromotionActive(job, now)) return 'Promoted';
  if (isFeaturedJob(job)) return 'Editorial feature';
  if (isAutomaticFeaturedCandidate(job)) return 'Automatic candidate';
  return 'Quality fallback';
};

export const compareDefaultJobs = (a, b, now = Date.now()) => {
  const tierDifference = presentationTier(b, now) - presentationTier(a, now);
  if (tierDifference) return tierDifference;

  const priorityDifference = asPriority(b?.listing_priority) - asPriority(a?.listing_priority);
  if (priorityDifference) return priorityDifference;

  const scoreDifference = smartScore(b) - smartScore(a);
  if (scoreDifference) return scoreDifference;

  const dateDifference = asTimestamp(b?.date_added || b?.created) - asTimestamp(a?.date_added || a?.created);
  if (dateDifference) return dateDifference;

  return String(a?.id || '').localeCompare(String(b?.id || ''));
};

const compareManualJobs = (a, b, now) => {
  const priorityDifference = asPriority(b?.listing_priority) - asPriority(a?.listing_priority);
  if (priorityDifference) return priorityDifference;
  return compareDefaultJobs(a, b, now);
};

const sourceKey = job => String(job?.source_type || 'unknown').trim().toLowerCase();
const employerKey = job => String(job?.luxury_brand || job?.store || job?.brand || 'unknown').trim().toLowerCase();

const balanceRankedJobs = rankedJobs => {
  const pool = [...rankedJobs];
  const balanced = [];

  while (pool.length) {
    const last = balanced.at(-1);
    const recentSources = balanced.slice(-2).map(sourceKey);
    const windowSize = Math.min(8, pool.length);
    const candidates = pool.slice(0, windowSize);
    let nextIndex = candidates.findIndex(job =>
      employerKey(job) !== employerKey(last) &&
      sourceKey(job) !== sourceKey(last) &&
      !recentSources.every(source => source === sourceKey(job))
    );
    if (nextIndex < 0) nextIndex = candidates.findIndex(job => employerKey(job) !== employerKey(last));
    if (nextIndex < 0) nextIndex = 0;
    balanced.push(pool.splice(nextIndex, 1)[0]);
  }

  return balanced;
};

export const orderJobsByPublicDefault = (jobs, orderMode = 'balanced', now = Date.now()) => {
  const mode = normalizePublicJobOrder(orderMode);
  const promoted = jobs.filter(job => isPromotionActive(job, now)).sort((a, b) => compareManualJobs(a, b, now));
  const remaining = jobs.filter(job => !isPromotionActive(job, now));

  if (mode === 'manual') return [...promoted, ...remaining.sort((a, b) => compareManualJobs(a, b, now))];
  if (mode === 'newest') return [...promoted, ...remaining.sort((a, b) => asTimestamp(b.date_added || b.created) - asTimestamp(a.date_added || a.created))];
  if (mode === 'luxury-score') return [...promoted, ...remaining.sort((a, b) => Number(b.luxury_score || 0) - Number(a.luxury_score || 0) || compareDefaultJobs(a, b, now))];
  if (mode === 'alphabetical') return [...promoted, ...remaining.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))];

  const priorityJobs = remaining.filter(job => asPriority(job.listing_priority) > 0).sort((a, b) => compareManualJobs(a, b, now));
  const automaticJobs = remaining.filter(job => asPriority(job.listing_priority) === 0).sort((a, b) => compareDefaultJobs(a, b, now));
  return [...promoted, ...priorityJobs, ...balanceRankedJobs(automaticJobs)];
};

export const sortJobsForAdmin = (jobs, sortBy = 'default', now = Date.now()) => {
  const sorted = [...jobs];
  sorted.sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return asTimestamp(b.date_added || b.created) - asTimestamp(a.date_added || a.created);
      case 'alphabetical':
        return String(a.title || '').localeCompare(String(b.title || ''));
      case 'luxury-score':
        return Number(b.luxury_score || 0) - Number(a.luxury_score || 0) || compareDefaultJobs(a, b, now);
      case 'promoted':
        return Number(isPromotionActive(b, now)) - Number(isPromotionActive(a, now)) || compareDefaultJobs(a, b, now);
      case 'featured':
        return Number(isFeaturedJob(b)) - Number(isFeaturedJob(a))
          || Number(isAutomaticFeaturedCandidate(b)) - Number(isAutomaticFeaturedCandidate(a))
          || compareDefaultJobs(a, b, now);
      case 'manual-priority':
        return asPriority(b.listing_priority) - asPriority(a.listing_priority) || compareDefaultJobs(a, b, now);
      default:
        return compareDefaultJobs(a, b, now);
    }
  });
  return sorted;
};
